import { createFileRoute, redirect } from '@tanstack/react-router'
import { getGetUsersProfileQueryOptions } from '@/types/origin-point-engine'
import { logger } from '@/lib/logger'
import { showErrorData } from '@/lib/toast'
import { AuthenticatedLayout } from '@/components/layout/authenticated-layout'
import { useAuthStore } from '@/features/auth/store/auth-store'
import type { UserInfoResponse } from '@/features/auth/types/type'

/**
 * 认证布局路由
 *
 * 职责：
 * 1. 检查用户是否已登录（有有效的 accessToken）
 * 2. 如果有 token 但没有 user 信息，自动调用 API 恢复用户信息
 * 3. 处理 token 过期的情况
 * 4. 确保所有子路由都能访问到完整的用户信息
 *
 * 技术细节：
 * - 使用 context.queryClient.ensureQueryData 来利用 TanStack Query 的缓存
 * - 避免在 beforeLoad 中使用 React Hooks（不是 React 组件环境）
 */
export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ preload, location, context }) => {
    const { auth, authDialog } = useAuthStore.getState()

    // Step 1: 检查是否有 token
    if (!auth.accessToken) {
      if (!preload) {
        authDialog.setOpen(true)
        showErrorData('请先登录')
      }

      throw redirect({
        to: '/',
        search: {
          redirect: location.href,
        },
      })
    }

    // Step 2: 如果有 token 但没有 user，尝试从后端恢复用户信息
    if (!auth.user) {
      try {
        logger.info('[Auth] Token 存在但 User 为空，尝试恢复用户信息')

        // 使用 queryClient.ensureQueryData 来利用缓存
        // 如果数据已在缓存中，直接返回；否则调用 API
        const queryOptions = getGetUsersProfileQueryOptions({
          query: {
            staleTime: 10 * 60 * 1000, // 10分钟内不重新请求
          },
        })

        const response = await context.queryClient.ensureQueryData(queryOptions)

        const userInfoResponse = response as unknown as UserInfoResponse
        const userData = userInfoResponse?.data?.user

        if (userData && userData.id) {
          logger.debug('[Auth] 用户信息恢复成功', { userId: userData.id })
          auth.setUser(userData)
        } else {
          logger.error('[Auth] 获取用户信息失败：响应为空', response)
          throw new Error('获取用户信息失败')
        }
      } catch (error) {
        // Token 无效或已过期，清除状态并重定向到登录
        logger.error('[Auth] 恢复用户信息失败，Token 可能已过期', error)
        auth.reset()

        if (!preload) {
          authDialog.setOpen(true)
          showErrorData('登录已过期，请重新登录')
        }

        throw redirect({
          to: '/',
          search: {
            redirect: location.href,
          },
        })
      }
    }

    // Step 3: 验证通过，继续渲染子路由
    const latestAuth = useAuthStore.getState().auth
    logger.debug('[Auth] 认证检查通过', {
      userId: latestAuth.user?.id,
      role: latestAuth.user?.role,
    })
  },
  component: AuthenticatedLayout,
})
